import React, { useEffect, useMemo } from 'react';

import DetachedDropdown, {
  detachedDropdownFallbackValue,
  DropdownValue,
} from '@/components/form/dropdown/DetachedDropdown';
import {
  useGameCategoryNavActions,
  useGameCategoryNavData,
} from '@/store/mexico/gamesCategoryNavStore';
import { useAbsoluteNavigate } from '@/hooks/useAbsoluteNavigate';
import { useDispatch, useSelector } from 'react-redux';
import { AbsoluteNavLink } from '@/components/AbsoluteNavLink';
import { IntlShape, useIntl } from 'react-intl';
import classNames from 'classnames';
import { useLocation } from 'react-use';
import { selectIsAuthenticated } from '@/store/selectors/authSelectors';
import { envIsCanada, envIsMexico, envIsNormal, envIsSweden } from '@/constants/constants';
import { selectIsFinOrSv, selectIsROC, selectIsThailand } from '@/store/selectors/commonSelectors';
import CategorySelector from '@/components/shared/games-category-nav/CategorySelector';
import { BffGamesCategory } from '@lucky7ventures/bff-types';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';
import { selectGameProvidersList } from '@/store/games/selectors';
import usePsaTopOffset from '@/hooks/usePsaTopOffset';
import { MEXICO_FILTERED_SHORTDESCRIPTIONS } from '@/utils/games-utils';
import {
  GAME_CATEGORY_NAV_LIVE_CASINO_CATEGORIES,
  GAME_CATEGORY_NAV_NORMAL_CATEGORIES,
  GAME_CATEGORY_NAV_ONTARIO_CATEGORIES,
  GAME_CATEGORY_NAV_ROC_CATEGORIES,
  GAME_CATEGORY_NAV_SHARED_CATEGORIES,
  GAMES_FEATURES_CANADA,
  GAMES_FEATURES_MEXICO,
  GAMES_FEATURES_NORMAL,
  GAMES_FEATURES_THAILAND,
  GIG_GAMES_CATEGORIES_RECORD,
} from '@/constants/games-constants';
import { selectGamesSearch, selectIsLobbyRoute } from '@/store/ui/selectors';
import { scrollToTop } from '@/utils/utils';
import { setSearchDisplayed } from '@/store/ui/actions';

const initialProviderOption = (intl: IntlShape) => ({
  label: '🕹️ ' + intl.formatMessage({ id: 'games.nav.providers' }),
  value: detachedDropdownFallbackValue,
});

const initialLiveCasinoGame = (intl: IntlShape) => ({
  label: '♠️ ' + intl.formatMessage({ id: 'games.nav.live-casino' }),
  value: detachedDropdownFallbackValue,
});

const initialCategoryOption = (intl: IntlShape) => ({
  label: '🎰 ' + intl.formatMessage({ id: 'games.nav.categories' }),
  value: detachedDropdownFallbackValue,
});

const initialFeatureOption = (intl: IntlShape) => ({
  label: '💎 ' + intl.formatMessage({ id: 'games.nav.features' }),
  value: detachedDropdownFallbackValue,
});

enum NavSections {
  Categories,
  CategoriesDropdown,
  Providers,
  Features,
  LiveCasino,
  GameTypes,
}

function GamesCategoryNav(): JSX.Element {
  const dispatch = useDispatch();
  const { isSearchDisplayed } = useSelector(selectGamesSearch);
  const { feature, provider, category, liveCasinoCategory } = useGameCategoryNavData();
  const { setFeature, setProvider, setCategory, setLiveCasinoCategory, reset } =
    useGameCategoryNavActions();
  const navigate = useAbsoluteNavigate();
  const location = useLocation();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const intl = useIntl();
  const isThailand = useSelector(selectIsThailand);
  const isROC = useSelector(selectIsROC);
  const isNormalNotRegulated = !useSelector(selectIsFinOrSv);
  const gameProvidersList = useSelector(selectGameProvidersList);
  const topOffset = usePsaTopOffset();
  const isLobbyRoute = useSelector(selectIsLobbyRoute(location.pathname ?? ''));

  const getProviderName = (slug: string): string => {
    return gameProvidersList.find(i => i.slug === slug)?.name || '';
  };

  // dynamic ordering of the main sections of the navbar
  const sectionOrder = useMemo(() => {
    if (envIsMexico()) {
      return [NavSections.CategoriesDropdown, NavSections.Features, NavSections.Providers];
    }

    if (envIsCanada()) {
      return [
        NavSections.CategoriesDropdown,
        NavSections.LiveCasino,
        NavSections.Providers,
        NavSections.Features,
        NavSections.GameTypes,
      ];
    }

    if (envIsNormal() && isThailand) {
      return [NavSections.Providers, NavSections.CategoriesDropdown, NavSections.Features];
    }

    if (envIsNormal() && isNormalNotRegulated) {
      return [
        NavSections.CategoriesDropdown,
        NavSections.LiveCasino,
        NavSections.Providers,
        NavSections.Features,
        NavSections.GameTypes,
      ];
    }

    return [NavSections.Categories];
  }, [isThailand, isNormalNotRegulated]);

  const providersDropdownOptions: DropdownValue[] = useMemo(() => {
    // Only apply Mexico filtering in Mexico environment
    const gameProviders = envIsMexico()
      ? gameProvidersList.filter(
          provider => !MEXICO_FILTERED_SHORTDESCRIPTIONS.includes(provider.name.toLowerCase()),
        )
      : gameProvidersList;

    if (!gameProviders || gameProviders.length === 0) {
      return [];
    }
    return gameProviders.reduce((acc: DropdownValue[], item) => {
      acc.push({
        label: item.name,
        value: item.slug,
      });
      return acc;
    }, []);
  }, [gameProvidersList]);

  // List of features for a dropdown
  // if empty the Features dropdown won't be shown
  const featuresList = useMemo(() => {
    if (envIsMexico()) {
      return [...GAMES_FEATURES_MEXICO];
    }

    if (envIsCanada()) {
      return [...GAMES_FEATURES_CANADA];
    }

    if (envIsNormal() && isThailand) {
      return [...GAMES_FEATURES_THAILAND];
    }

    if (envIsNormal() && isNormalNotRegulated) {
      return [...GAMES_FEATURES_NORMAL];
    }

    return [];
  }, [isThailand, isNormalNotRegulated]);

  const featuresDropdownOptions: DropdownValue[] = useMemo(() => {
    return featuresList.reduce((acc: DropdownValue[], feature) => {
      acc.push({
        label: intl.formatMessage({ id: `games.categories.${feature}` }),
        value: feature,
      });
      return acc;
    }, []);
  }, [intl, featuresList]);

  // Set categories for each market based on conditions
  // Complicated logic, will be improved in WL with configuration
  const gameCategories = useMemo(() => {
    let categoriesList: BffGamesCategory[] = [];
    let categories: { label: string; value: string }[] = [];
    if (envIsCanada()) {
      categoriesList = GAME_CATEGORY_NAV_ONTARIO_CATEGORIES;
    } else if (isROC) {
      categoriesList = GAME_CATEGORY_NAV_ROC_CATEGORIES;
    } else if (isNormalNotRegulated && !isThailand && !isROC) {
      categoriesList = GAME_CATEGORY_NAV_NORMAL_CATEGORIES;
    } else if (envIsMexico() || envIsSweden() || isThailand || !isNormalNotRegulated) {
      categoriesList = GAME_CATEGORY_NAV_SHARED_CATEGORIES;
    }

    categories = categoriesList.map(category => ({
      label: intl.formatMessage({ id: GIG_GAMES_CATEGORIES_RECORD[category] }),
      value: category,
    }));

    // When logged in, user needs to see Last Played and Favorites
    if (isAuthenticated) {
      categories.unshift(
        {
          label: intl.formatMessage({
            id: GIG_GAMES_CATEGORIES_RECORD[BffGamesCategory.FavouriteGames],
          }),
          value: BffGamesCategory.FavouriteGames,
        },
        {
          label: intl.formatMessage({
            id: GIG_GAMES_CATEGORIES_RECORD[BffGamesCategory.LastPlayed],
          }),
          value: BffGamesCategory.LastPlayed,
        },
      );
    }

    return categories;
  }, [isAuthenticated, isThailand, isNormalNotRegulated, isROC]);

  const handleCategoryChange = (category: string) => {
    dispatch(setSearchDisplayed(false));
    triggerGTag(GTagEvents.games_nav_category, {
      slug: category,
    });
    navigate('/games/' + category);
    setCategory(category as BffGamesCategory);
  };

  const liveCasinoCategories: DropdownValue[] = useMemo(() => {
    if (!sectionOrder.includes(NavSections.LiveCasino)) return [];
    return GAME_CATEGORY_NAV_LIVE_CASINO_CATEGORIES.map(liveCasinoCat => ({
      label: intl.formatMessage({ id: GIG_GAMES_CATEGORIES_RECORD[liveCasinoCat] }),
      value: liveCasinoCat,
    }));
  }, [sectionOrder]);

  const handleSelectLiveCasinoCategory = (liveCasinoCategoryChange: string) => {
    dispatch(setSearchDisplayed(false));
    triggerGTag(GTagEvents.games_nav_live_casino_category, {
      slug: liveCasinoCategoryChange,
    });
    navigate('/games/' + liveCasinoCategoryChange);
    setLiveCasinoCategory(liveCasinoCategoryChange as BffGamesCategory);
  };

  const handleFeatureChange = (feature: string) => {
    dispatch(setSearchDisplayed(false));
    triggerGTag(GTagEvents.games_nav_feature, {
      slug: feature,
    });
    if (feature === BffGamesCategory.BonusBuy && envIsCanada()) {
      navigate('/games/buy-feature');
      setFeature(feature as BffGamesCategory);
      return;
    }
    if (feature !== detachedDropdownFallbackValue) {
      navigate('/games/' + feature);
      setFeature(feature as BffGamesCategory);
    } else {
      setFeature(undefined);
      navigate('/games');
    }
  };

  const handleProviderChange = (provider: string) => {
    dispatch(setSearchDisplayed(false));
    triggerGTag(GTagEvents.games_nav_provider, {
      slug: provider,
    });
    if (provider !== detachedDropdownFallbackValue) {
      navigate(`/games/provider/${provider}`);
      setProvider(provider);
    } else {
      setProvider(undefined);
      navigate('/games');
    }
  };

  const onSearchClick = () => {
    triggerGTag(GTagEvents.game_header_nav_search);
    if (location.pathname !== '/games') {
      navigate('/games');
    }
    // Have to put it in timeout so it runs after navigate
    setTimeout(() => {
      scrollToTop();
      reset();
      dispatch(setSearchDisplayed(true));
    }, 0);
  };

  // effect which tries to set the initial provider/feature/category if somebody lands on a specific url
  useEffect(() => {
    dispatch(setSearchDisplayed(false));
    // to try and find a slug of a category/provider/feature we look for the url segment after the /games/
    const urlChunks = location?.pathname?.split('/');

    // check if games is one of the chunks
    if (!urlChunks || !urlChunks.includes('games')) {
      return;
    }

    // if games is one of the chunks check what is after the games
    const gamesChunkIndex = urlChunks.indexOf('games');

    //check if one of the provider slugs is in the url
    // return if there is nothing after the games
    if (urlChunks.length === gamesChunkIndex) {
      return;
    }

    // if there is something after the games it could be the slug
    const providersSet = new Set(gameProvidersList.map(i => i.slug));
    const providerInUrl = urlChunks.find(item => providersSet.has(item));
    // check if the url slug matches to one of the providers - set the provider as selected
    if (providerInUrl) {
      setProvider(providerInUrl);
    }

    // check if the url slug matches one of the features - set the feature as selected
    const featuresSet = new Set(featuresList);
    const featureInUrl = urlChunks.find(item => featuresSet.has(item as BffGamesCategory));
    if (featureInUrl) {
      setFeature(featureInUrl as BffGamesCategory);
    }

    // check if the url slug matches one of the categories - set the category as selected
    const categoriesSet = new Set(gameCategories.map(i => i.value));
    const categoryInUrl = urlChunks.find(item => categoriesSet.has(item as BffGamesCategory));
    if (categoryInUrl) {
      setCategory(categoryInUrl as BffGamesCategory);
    }

    // check if the url slug matches one of the live casino categories - set the live casino category as selected
    const liveCasinoCategoriesSet = new Set(liveCasinoCategories.map(i => i.value));
    const liveCasinoCategoryInUrl = urlChunks.find(item =>
      liveCasinoCategoriesSet.has(item as BffGamesCategory),
    );
    if (liveCasinoCategoryInUrl) {
      setLiveCasinoCategory(liveCasinoCategoryInUrl as BffGamesCategory);
    }

    return () => reset();
  }, [location, gameProvidersList, featuresList]);

  return (
    <div className="sticky z-[90] w-full shadow-md" style={{ top: `${topOffset}px` }}>
      <div className="flex overflow-y-visible overflow-x-auto items-center no-scrollbar justify-start lg:justify-center gap-x-2 bg-white py-3 px-4 md:px-8 text-sm">
        {!envIsSweden() && (
          <div
            className={classNames('p-[8px] mr-1.5 rounded-full cursor-pointer', {
              'bg-black/5': !isSearchDisplayed,
              'bg-blue-blue text-white': isSearchDisplayed,
            })}
            onClick={onSearchClick}
          >
            &#128269;
          </div>
        )}
        <AbsoluteNavLink
          to="/games"
          onClick={() => {
            dispatch(setSearchDisplayed(false));
            reset();
          }}
          className={classNames(
            'flex flex-shrink-0 gap-x-2 items-center font-bold rounded-3xl py-2 px-4',
            {
              '[&.active]:bg-blue-light [&.active]:text-white': isLobbyRoute && !isSearchDisplayed,
              'bg-gray-100 text-black': location.pathname !== '/games' || isSearchDisplayed,
            },
          )}
          end
        >
          🏠 {intl.formatMessage({ id: 'games.categories.lobby' })}
        </AbsoluteNavLink>
        {sectionOrder.map(section => {
          if (section === NavSections.Categories) {
            return (
              <CategorySelector
                key={`games-nav-categories-${section}`}
                categories={gameCategories}
                selectedCategory={category}
                onCategoryChange={handleCategoryChange}
                onReset={reset}
              />
            );
          }
          if (section === NavSections.CategoriesDropdown) {
            return (
              <DetachedDropdown
                data-cy="category-dropdown"
                key="games-nav-categories-dropdown"
                label={intl.formatMessage({ id: 'games.nav.categories' })}
                options={gameCategories}
                value={
                  category
                    ? {
                        value: category,
                        label:
                          '🎰 ' + intl.formatMessage({ id: GIG_GAMES_CATEGORIES_RECORD[category] }),
                      }
                    : initialCategoryOption(intl)
                }
                onChange={handleCategoryChange}
              />
            );
          }
          if (section === NavSections.Providers) {
            return (
              <DetachedDropdown
                key="games-nav-providers"
                label={intl.formatMessage({ id: 'games.nav.providers' })}
                options={providersDropdownOptions}
                value={
                  provider
                    ? { value: provider, label: '🕹️ ' + getProviderName(provider) }
                    : initialProviderOption(intl)
                }
                onChange={handleProviderChange}
              />
            );
          }
          if (section === NavSections.Features) {
            return (
              <DetachedDropdown
                key="games-nav-features"
                label={intl.formatMessage({ id: 'games.nav.features' })}
                options={featuresDropdownOptions}
                value={
                  feature
                    ? {
                        value: feature,
                        label: '💎 ' + intl.formatMessage({ id: `games.categories.${feature}` }),
                      }
                    : initialFeatureOption(intl)
                }
                onChange={handleFeatureChange}
              />
            );
          }
          if (section === NavSections.LiveCasino) {
            return (
              <DetachedDropdown
                key="games-nav-live-casino"
                label={intl.formatMessage({ id: 'games.nav.live-casino' })}
                options={liveCasinoCategories}
                value={
                  liveCasinoCategory
                    ? {
                        value: liveCasinoCategory,
                        label:
                          '♠️ ' +
                          intl.formatMessage({
                            id: GIG_GAMES_CATEGORIES_RECORD[liveCasinoCategory],
                          }),
                      }
                    : initialLiveCasinoGame(intl)
                }
                onChange={handleSelectLiveCasinoCategory}
              />
            );
          }
          return <></>;
        })}
      </div>
    </div>
  );
}

export default GamesCategoryNav;
